import apiClient from './apiClient';

export type PriceType = 'particular' | 'ars' | 'coop' | 'mipyme' | 'other';

export const PRICE_TYPE_LABELS: Record<PriceType, string> = {
  particular: 'Particular',
  ars: 'ARS',
  coop: 'Cooperativa',
  mipyme: 'MIPYME',
  other: 'Otro',
};

export interface ServicePrice {
  id: number;
  service_id: number;
  insurance_company_id: number | null;
  price_type: PriceType;
  price: string;
  cost_price: string | null;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  insuranceCompany?: { id: number; name: string };
}

export interface CreateServicePriceRequest {
  service_id: number;
  insurance_company_id?: number | null;
  price_type: PriceType;
  price: number;
  cost_price?: number | null;
  start_date?: string;
  end_date?: string | null;
  is_active?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  per_page: number;
  current_page: number;
}

interface QuoteResponse {
  service_id: number;
  particular: { price: string; cost_price: string | null } | null;
  insurance?: { company_id: number; price: string; cost_price: string | null } | null;
  insurances?: { company_id: number; company_name: string; price: string; cost_price: string | null }[];
}

export const servicePriceService = {
  async byService(serviceId: number): Promise<ServicePrice[]> {
    const res = await apiClient.get<ApiResponse<ServicePrice[]>>(`/service-prices/service/${serviceId}`);
    return res.data.data ?? [];
  },

  async getAll(params: {
    service_id?: number;
    insurance_company_id?: number;
    price_type?: PriceType;
    is_active?: boolean;
    particular?: boolean;
    per_page?: number;
  } = {}): Promise<PaginatedResponse<ServicePrice>> {
    const q = new URLSearchParams();
    if (params.service_id) q.append('service_id', String(params.service_id));
    if (params.insurance_company_id) q.append('insurance_company_id', String(params.insurance_company_id));
    if (params.price_type) q.append('price_type', params.price_type);
    if (params.is_active !== undefined) q.append('is_active', String(params.is_active));
    if (params.particular) q.append('particular', 'true');
    if (params.per_page) q.append('per_page', String(params.per_page));
    const res = await apiClient.get<PaginatedResponse<ServicePrice>>(`/service-prices?${q.toString()}`);
    return res.data;
  },

  async create(data: CreateServicePriceRequest): Promise<ServicePrice> {
    const res = await apiClient.post<ApiResponse<ServicePrice>>('/service-prices', data);
    return res.data.data;
  },

  async update(id: number, data: Partial<CreateServicePriceRequest>): Promise<ServicePrice> {
    const res = await apiClient.put<ApiResponse<ServicePrice>>(`/service-prices/${id}`, data);
    return res.data.data;
  },

  async deactivate(id: number): Promise<ServicePrice> {
    const res = await apiClient.put<ApiResponse<ServicePrice>>(`/service-prices/${id}`, { is_active: false });
    return res.data.data;
  },

  async destroy(id: number): Promise<void> {
    await apiClient.delete(`/service-prices/${id}`);
  },

  async getQuote(serviceId: number, insuranceCompanyId?: number): Promise<QuoteResponse> {
    const q = new URLSearchParams({ service_id: String(serviceId) });
    if (insuranceCompanyId) q.append('insurance_company_id', String(insuranceCompanyId));
    const res = await apiClient.get<ApiResponse<QuoteResponse>>(`/service-prices/quote?${q.toString()}`);
    return res.data.data;
  },
};

export default servicePriceService;
